import { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDebounce } from 'use-debounce';
import { useMask } from '@react-input/mask';
import { FaCheck } from 'react-icons/fa';
import { V2QsPhoneNumberProps } from './types';

const PHONE_MASK = '(___) ___-____';

/**
 * V2QsPhoneNumber 컴포넌트
 *
 * 전화번호 입력을 위한 컴포넌트 (US 포맷)
 * V1 QsInputPhoneNumber의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <V2QsPhoneNumber
 *   value={phone}
 *   onChange={setPhone}
 * />
 *
 * // 인증 기능 포함
 * <V2QsPhoneNumber
 *   value={phone}
 *   onChange={setPhone}
 *   showVerification
 *   isVerified={isVerified}
 *   isVerifying={isVerifying}
 *   onVerifyClick={handleVerify}
 * />
 * ```
 */
function V2QsPhoneNumber({
  value = '',
  onChange,
  debounceMs = 150,
  placeholder = PHONE_MASK,
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  autoFocus = false,
  showVerification = false,
  isVerified = false,
  isVerifying = false,
  onVerifyClick,
  verifyButtonText = 'Verify',
  verifiedButtonText = 'Verified',
}: V2QsPhoneNumberProps) {
  const [localValue, setLocalValue] = useState(value || '');
  const [debouncedValue] = useDebounce(localValue, debounceMs);

  // Phone mask 설정
  const maskRef = useMask({
    mask: PHONE_MASK,
    replacement: { _: /\d/ },
    showMask: true,
  });

  // 외부 value 변경 시 동기화
  useEffect(() => {
    if (value !== localValue && value !== undefined) {
      setLocalValue(value);
    }
  }, [value]);

  // 디바운스된 값 변경 시 onChange 호출
  useEffect(() => {
    if (debouncedValue !== value) {
      // 마스크만 있는 경우 빈 값으로 처리
      const cleanValue = debouncedValue === PHONE_MASK ? '' : debouncedValue;
      onChange?.(cleanValue);
    }
  }, [debouncedValue]);

  // 유효한 전화번호인지 확인 (10자리 숫자)
  const isValidPhone = localValue.replace(/\D/g, '').length === 10;

  const hasError = error;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Input wrapper 클래스
  const inputWrapperClasses = classNames(
    'flex items-center transition-all duration-200 rounded-[10px]',
    'border-[1.5px]',
    {
      // Width
      'w-full': fullWidth,
      'w-[260px]': !fullWidth,

      // Border color
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError && !readOnly,
      'focus-within:border-[#4B7BF5]': !disabled && !hasError,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,
    }
  );

  // Input 클래스
  const inputClasses = classNames(
    'flex-1 px-5 py-[17px] bg-transparent outline-none',
    'text-[15px] font-medium leading-[1.6]',
    {
      // Text color
      'text-[#1D1D1F]': !disabled,
      'text-[#AFB2C2]': disabled,

      // Placeholder
      'placeholder:text-[#AFB2C2]': true,

      // Cursor
      'cursor-not-allowed': disabled,
    },
    className
  );

  // Verify 버튼 클래스
  // 브랜드 컬러: patientTheme primary #3455FC
  const verifyButtonClasses = classNames(
    'flex items-center justify-center px-4 py-2 mr-2 rounded-lg',
    'text-sm font-medium uppercase transition-all duration-200',
    {
      // 인증 완료 상태
      'bg-[#E8F5E9] text-[#2E7D32]': isVerified,
      // 인증 가능 상태
      'bg-[#3455FC] text-white hover:bg-[#2A44CA]': !isVerified && isValidPhone && !isVerifying,
      // 비활성화 상태
      'bg-[#E6E9F5] text-[#AFB2C2] cursor-not-allowed': !isVerified && (!isValidPhone || isVerifying),
    }
  );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Input Wrapper */}
      <div className={inputWrapperClasses}>
        {/* Phone Icon */}
        <svg
          className="w-5 h-5 ml-5 flex-shrink-0 text-[#AFB2C2]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>

        {/* Input */}
        <input
          ref={maskRef}
          type="tel"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled || isVerifying}
          readOnly={readOnly}
          autoComplete="tel"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          className={inputClasses}
        />

        {/* Verify Button */}
        {showVerification && (
          <button
            type="button"
            className={verifyButtonClasses}
            disabled={!isValidPhone || isVerifying || isVerified}
            onClick={onVerifyClick}
          >
            {isVerifying && (
              <span className="loading loading-spinner loading-sm mr-2" />
            )}
            {isVerified && <FaCheck className="mr-2" />}
            {isVerified ? verifiedButtonText : verifyButtonText}
          </button>
        )}
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsPhoneNumber);
